import { FormikProps } from "formik"
import React from "react"
import { FormGroup, Col, Label, Input, FormFeedback, InputGroup, InputGroupText } from "reactstrap"
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Link } from "react-router-dom";
import { strategies } from "constants/campaign";
import RadioGroup from "Components/input/RadioGroup/RadioGroup";
import CampaignIcon from "assets/icons/CampaignIcon";
import DocIcon from "assets/icons/DocIcon";
import StarIcon from "assets/icons/StarIcon";
import AccessControl from "wrappers/AccessControl";
import { PRIVILEGE } from "constants/privilege";
import FieldInstruction from "Components/Common/FieldInstruction";
import { FIELDS_INSTRUCTIONS } from "constants/fieldsInstructions";
import RichTextEditor from "Components/Common/RichTextEditor";
import BrandSelection from "Components/Common/BrandSelection";
import { UserBrand } from "types/users";
import WebIcon from "assets/icons/WebIcon";
import GalleryIcon from "assets/icons/GalleryIcon";
import DollarIcon from "assets/icons/DollarIcon";

export interface CampaignDetailsFields {
    name: string,
    description: string,
    brand_id: number | string,
    brand: UserBrand | null,
    strategy: string,
    budget: string | number,
    total_budget: string | number | null,
    barter_budget: string | number | null,
    barter_reimbursement: boolean,
    landing_page_url: string
    content_media_types: {
        REELS: number,
        FEED: number,
        STORY: number
    },
    mode: 'workflow' | 'offline'
}

const CampaignDetails: React.FC<{ formFields: FormikProps<CampaignDetailsFields> }> = ({ formFields }) => {

    const userBrands = useSelector((state: RootState) => state.userBrands.items);

    return (
        <React.Fragment>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3">
                                <CampaignIcon />
                            </span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Campaign Name
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.NAME} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Input
                        type='text'
                        name='name'
                        className='form-control'
                        onChange={formFields.handleChange}
                        onBlur={formFields.handleBlur}
                        value={formFields.values.name || ""}
                        invalid={formFields.touched.name && formFields.errors.name ? true : false}
                        placeholder='Campaign name' />
                    {
                        formFields.touched.name && formFields.errors.name &&
                        <FormFeedback type="invalid">
                            {formFields.errors.name}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <DocIcon className='me-3' />
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Campaign Description
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.DESCRIPTION} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <RichTextEditor
                        placeholder="Write here about campaign"
                        value={formFields.values.description ?? ''}
                        onChange={(value) => formFields.setFieldValue('description', value)}
                        onBlur={() => formFields.setFieldTouched('description', true, true)}
                    />
                    {
                        formFields.touched.description && formFields.errors.description &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.description}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <StarIcon className='me-3' />
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Select Brand
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.BRAND} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <BrandSelection
                        name="brand_id"
                        value={formFields.values.brand ? { value: formFields.values.brand.id, label: formFields.values.brand.name } : null}
                        onChange={(userBrand) => {
                            formFields.setFieldValue('brand_id', userBrand?.value ?? '')
                            formFields.setFieldValue('brand', userBrand ? { name: userBrand.label, id: userBrand.value } : null)
                        }}
                        onBlur={() => formFields.setFieldTouched('brand_id', true, true)}
                    />
                    {
                        formFields.touched.brand_id && formFields.errors.brand_id &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.brand_id}
                        </FormFeedback>
                    }
                    <AccessControl privilege={PRIVILEGE.BRAND.BRAND.CREATE}>
                        <div className="d-flex justify-content-end mt-2">
                            <Link to='/brand/create' target="_blank" className="text-primary">+ Create Brand</Link>
                        </div>
                    </AccessControl>
                </Col>
            </FormGroup>
            {/* <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><StrategyIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Strategy
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.STRATEGY} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <RadioGroup
                        name="strategy"
                        options={Object.entries(strategies).map(([key, value]) => ({ id: key, label: value }))}
                        defaultValue={formFields.values.strategy ?? 'paid'}
                        onChange={(strategy) => {
                            formFields.setFieldValue('strategy', strategy)
                        }}
                    />
                </Col>
            </FormGroup> */}
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><WebIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='offline' className='form-label input-label mb-0'>
                                Track Only
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.MODE} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <RadioGroup
                        name="offline"
                        options={[{ id: 'y', label: 'Yes' }, { id: 'n', label: 'No' }]}
                        defaultValue={formFields?.values?.mode == 'offline' ? 'y' : 'n'}
                        onChange={(offline) => {
                            formFields.setValues({ ...formFields.values, mode: offline === 'y' ? 'offline' : 'workflow' })
                        }}
                    />
                </Col>
            </FormGroup>
            {
                formFields.values?.mode !== 'offline' &&
                <React.Fragment>
                    <div className="divider"></div>
                    <FormGroup row className='py-4 mt-3'>
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><DollarIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='total_budget' className='form-label input-label mb-0'>
                                        Overall Budget (INR)
                                        <span className="text-danger"> *</span>
                                    </Label>
                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.BUDGET} />
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1'>
                            <div className="input-container">
                                <span className="icon-circle input-icon">₹</span>
                                <Input
                                    type='number'
                                    name='total_budget'
                                    className='form-control budget-input'
                                    onChange={formFields.handleChange}
                                    onBlur={formFields.handleBlur}
                                    value={formFields.values.total_budget || ""}
                                    invalid={formFields.touched.total_budget && formFields.errors.total_budget ? true : false}
                                    placeholder='Enter Budget' />
                            </div>
                            {
                                formFields.touched.total_budget && formFields.errors.total_budget &&
                                <FormFeedback type="invalid" className="d-block">
                                    {formFields.errors.total_budget}
                                </FormFeedback>
                            }
                        </Col>
                    </FormGroup>
                </React.Fragment>
            }
            {/* <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><RupeeIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='budget' className='form-label input-label mb-0'>
                                Influencer Budget (INR)
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.PER_INFLUENCER_BUDGET} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <div className="input-container">
                        <span className="icon-circle input-icon">₹</span>
                        <Input
                            type='number'
                            name='budget'
                            className='form-control budget-input'
                            onChange={formFields.handleChange}
                            onBlur={formFields.handleBlur}
                            value={formFields.values.budget ?? ""}
                            invalid={formFields.touched.budget && formFields.errors.budget ? true : false}
                            placeholder='Enter Budget' />
                    </div>
                    {
                        formFields.touched.budget && formFields.errors.budget &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.budget}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup> */}
            {/* <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><RupeeIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='barter_budget' className='form-label input-label mb-0'>
                                Barter Budget (INR)
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.BARTER_BUDGET} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <div className="input-container">
                        <span className="icon-circle input-icon">₹</span>
                        <Input
                            type='number'
                            name='barter_budget'
                            className='form-control budget-input'
                            onChange={formFields.handleChange}
                            onBlur={formFields.handleBlur}
                            value={formFields.values.barter_budget ?? ""}
                            invalid={formFields.touched.barter_budget && formFields.errors.barter_budget ? true : false}
                            placeholder='Enter Budget' />

                    </div>
                    <div className="mt-2">
                        <Input
                            type='checkbox'
                            name='barter_reimbursement'
                            className='form-check-input'
                            id="barter_reimbursement"
                            onChange={formFields.handleChange}
                            onBlur={formFields.handleBlur}
                            checked={formFields.values.barter_reimbursement ?? false}
                            invalid={formFields.touched.barter_reimbursement && formFields.errors.barter_reimbursement ? true : false}
                        />
                        <label htmlFor="barter_reimbursement" className="ms-2 barter-reimbursement-label"> Barter reimbursement applicable?</label>
                    </div>
                    {
                        formFields.touched.barter_budget && formFields.errors.barter_budget &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.barter_budget}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup> */}
            {/* <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3">
                                <WebIcon />
                            </span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Target Link
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.LANDING_PAGE_URL} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Input
                        type='text'
                        name='landing_page_url'
                        className='form-control'
                        onChange={formFields.handleChange}
                        onBlur={formFields.handleBlur}
                        value={formFields.values.landing_page_url || ""}
                        invalid={formFields.touched.landing_page_url && formFields.errors.landing_page_url ? true : false}
                        placeholder='Target Link' />
                    {
                        formFields.touched.landing_page_url && formFields.errors.landing_page_url &&
                        <FormFeedback type="invalid">
                            {formFields.errors.landing_page_url}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup> */}
            {/* <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3">
                                <GalleryIcon />
                            </span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Content Deliverables
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.CONTENT_MEDIA_TYPES} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <InputGroup>
                        <InputGroupText>
                            REELS
                        </InputGroupText>
                        <Input
                            type='number'
                            name='REELS'
                            className='form-control border-radius-0'
                            onChange={(e) => {
                                let contentMediaTypes = { ...formFields.values.content_media_types, REELS: e.target.value };
                                formFields.setFieldValue('content_media_types', contentMediaTypes)
                            }}
                            onBlur={() => {
                                formFields.setFieldTouched('content_media_types')
                            }}
                            value={formFields.values.content_media_types.REELS || ""}
                            invalid={formFields.touched.content_media_types && formFields.errors.content_media_types?.REELS ? true : false}
                            placeholder='REELS' />
                        <InputGroupText>
                            FEED
                        </InputGroupText>
                        <Input
                            type='number'
                            name='FEED'
                            className='form-control border-radius-0'
                            onChange={(e) => {
                                let contentMediaTypes = { ...formFields.values.content_media_types, FEED: e.target.value };
                                formFields.setFieldValue('content_media_types', contentMediaTypes)
                            }}
                            onBlur={() => {
                                formFields.setFieldTouched('content_media_types')
                            }}
                            value={formFields.values.content_media_types.FEED ?? ""}
                            invalid={formFields.touched.content_media_types && formFields.errors.content_media_types?.FEED ? true : false}
                            placeholder='FEED' />
                        <InputGroupText>
                            STORY
                        </InputGroupText>
                        <Input
                            type='number'
                            name='STORY'
                            className='form-control border-radius-0'
                            onChange={(e) => {
                                let contentMediaTypes = { ...formFields.values.content_media_types, STORY: e.target.value };
                                formFields.setFieldValue('content_media_types', contentMediaTypes)
                            }}
                            onBlur={() => {
                                formFields.setFieldTouched('content_media_types')
                            }}
                            value={formFields.values.content_media_types.STORY ?? ""}
                            invalid={formFields.touched.content_media_types && formFields.errors.content_media_types?.STORY ? true : false}
                            placeholder='STORY' />
                    </InputGroup>
                    {
                        formFields.touched.content_media_types && formFields.errors.content_media_types?.REELS &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.content_media_types?.REELS}
                        </FormFeedback>
                    }
                    {
                        formFields.touched.content_media_types && formFields.errors.content_media_types?.FEED &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.content_media_types?.FEED}
                        </FormFeedback>
                    }
                    {
                        formFields.touched.content_media_types && formFields.errors.content_media_types?.STORY &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.content_media_types?.STORY}
                        </FormFeedback>
                    }
                    {
                        formFields.errors.content_media_types && typeof formFields.errors.content_media_types === 'string'  &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.content_media_types}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup> */}
        </React.Fragment>
    )
}

export default CampaignDetails